import { z } from "zod";
import { formProfileInputSchema, formProfileNameSchema } from "./form-profiles";
import type { Principal } from "./repository";

const ids = z
  .array(z.string().trim().min(1).max(200))
  .max(1000)
  .refine(
    (values) => new Set(values).size === values.length,
    "Duplicate audience identity",
  );
/** Membership claims are supplied by the authenticated host, never by a form submission. */
export const profileAudienceSchema = z
  .object({
    users: ids.default([]),
    groups: ids.default([]),
    companies: ids.default([]),
    roles: ids.default([]),
    workspace: z.boolean().default(false),
  })
  .strict();
export const predefinedProfileSchema = z
  .object({
    id: z.string().uuid(),
    name: formProfileNameSchema,
    active: z.boolean().default(true),
    values: formProfileInputSchema.shape.values,
    audience: profileAudienceSchema,
  })
  .strict();
export const profileDatasetSchema = z
  .object({
    resourceId: z.string().min(1).max(200),
    resourceVersion: z.number().int().positive(),
    records: z.array(predefinedProfileSchema).max(1000),
  })
  .strict()
  .refine(
    (value) =>
      new Set(value.records.map((row) => row.id)).size === value.records.length,
    "Duplicate global profile identity",
  );
export const profileDatasetWriteSchema = profileDatasetSchema.safeExtend({
  expectedRevision: z.number().int().min(0),
});
export type PredefinedProfile = z.infer<typeof predefinedProfileSchema>;
export type ProfileDataset = z.infer<typeof profileDatasetSchema>;
export type ProfileDatasetView = ProfileDataset & {
  revision: number;
  durable: boolean;
};
export type ProfileDatasetWrite = z.input<typeof profileDatasetWriteSchema>;
/** Targets are ORed; an optional role restriction is ANDed with that audience. */
export function canUsePredefinedProfile(
  profile: PredefinedProfile,
  actor: Principal,
) {
  const audience = profile.audience;
  const names = {
    editor: "tenant.editor",
    tester: "tenant.tester",
    assetManager: "tenant.asset-manager",
    administrator: "tenant.administrator",
  };
  const roles: string[] = [
    ...actor.roles,
    ...actor.roles.map((role) => names[role]),
    ...(actor.authorization?.roleIds || []),
  ];
  const roleMatch =
    !audience.roles.length ||
    audience.roles.some((role) => roles.includes(role));
  const targeted =
    audience.users.length + audience.groups.length + audience.companies.length;
  return (
    profile.active &&
    roleMatch &&
    (audience.workspace ||
      audience.users.includes(actor.subject) ||
      audience.groups.some((id) =>
        actor.profileMemberships?.groupIds.includes(id),
      ) ||
      audience.companies.some((id) =>
        actor.profileMemberships?.companyIds.includes(id),
      ) ||
      (!targeted && audience.roles.length > 0))
  );
}
